import * as Sentry from '@sentry/vue'

export interface ErrorOptions {
  message: string
  status?: number
  name?: string
  code?: string
  details?: any
  originalError?: Error
  captureInSentry?: boolean
}

export class NabooError extends Error {
  public readonly message: string
  public readonly status: number
  public readonly code: string
  public readonly details?: any
  public readonly originalError?: Error
  private _sentryReported = false

  constructor(options: ErrorOptions) {
    super(options.message)

    // Définition des propriétés de base
    this.message = options.message
    this.status = options.status || 500
    this.name = options.name || 'UnifiedError'
    this.code = options.code || 'ERROR_UNKNOWN'
    this.details = options.details
    this.originalError = options.originalError

    // Capture de la stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NabooError)
    }

    // Si l'erreur originale a une stack trace, on peut la préserver
    if (this.originalError?.stack) {
      this.stack = `${this.stack}\nCaused by: ${this.originalError.stack}`
    }

    if (options.captureInSentry) {
      this.captureToSentry()
    }
  }

  /**
   * Vérifie si l'erreur a déjà été envoyée à Sentry
   */
  public get isSentryReported(): boolean {
    return this._sentryReported
  }

  /**
   * Envoie l'erreur à Sentry avec des informations contextuelles
   */
  public captureToSentry(): void {
    if (this._sentryReported) {
      return // Évite les doublons
    }

    Sentry.captureException(this, {
      tags: {
        errorCode: this.code,
        errorStatus: this.status.toString()
      },
      extra: {
        details: this.details,
        originalError: this.originalError
          ? {
              message: this.originalError.message,
              name: this.originalError.name,
              stack: this.originalError.stack
            }
          : undefined
      }
    })

    this._sentryReported = true
  }

  /**
   * Crée une erreur à partir d'une erreur d'API Axios
   */
  public static fromAxiosError(
    error: any,
    defaultMessage = 'Une erreur réseau est survenue'
  ): NabooError {
    console.warn('NabooError.fromAxiosError', error)

    const isAxiosError = error && error.isAxiosError === true

    if (!isAxiosError) {
      return new NabooError({
        message: error?.message || defaultMessage,
        name: 'NetworkError',
        code: 'NETWORK_ERROR',
        originalError: error instanceof Error ? error : new Error(defaultMessage)
      })
    }

    const status = error.response?.status
    const apiMessage = error.response?.data?.message || error.message || defaultMessage
    const apiCode = error.response?.data?.code || 'API_ERROR'

    return new NabooError({
      message: apiMessage,
      status: status,
      name: 'ApiError',
      code: `API_${status || 'UNKNOWN'}_${apiCode}`,
      details: error.response?.data,
      originalError: error
    })
  }

  /**
   * Format détaillé de l'erreur pour le logging
   */
  public getFormattedDetails(includeTimestamp = false): string {
    const timestamp = includeTimestamp ? ` - ${new Date().toISOString()}` : ''
    return `[${this.code}] ${this.name} (${this.status}): ${this.message}${timestamp}`
  }

  /**
   * Crée une version utilisateur de l'erreur (sans détails techniques)
   */
  public getUserFriendlyError(): { message: string; code: string } {
    // Mappage des codes d'erreur aux messages utilisateur
    const userMessages: Record<string, string> = {
      API_401_UNAUTHORIZED: 'Votre session a expiré, veuillez vous reconnecter.',
      API_403_FORBIDDEN: "Vous n'avez pas les droits nécessaires pour effectuer cette action.",
      API_404_NOT_FOUND: "La ressource demandée n'a pas été trouvée.",
      API_500_SERVER_ERROR: 'Le serveur a rencontré une erreur, veuillez réessayer plus tard.',
      NETWORK_ERROR: 'Problème de connexion, veuillez vérifier votre réseau.'
    }

    // Recherche d'un message spécifique ou utilisation du message par défaut
    const userMessage =
      userMessages[this.code] ||
      (this.code.startsWith('API_')
        ? 'Une erreur est survenue côté serveur.'
        : 'Une erreur inattendue est survenue.')

    return {
      message: userMessage,
      code: this.code
    }
  }
}
